import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { searchClient } from "../../../services/admin/client/client";
import handleError from "../../../utils/handleError";
import { useDispatch, useSelector } from "react-redux";
import ClientTableSkeleton from "../../../components/Skeletons/ClientTableSkeleton";
import _ from "lodash";
import { encrypt } from "../../../utils/commonLogics";
import Pagination from "../../../utils/admin/pagination/Pagination";
import { deleteUser, getUserData } from "../../../services/user/manageusers";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

const UsersList = () => {
  const toast = useRef(null);
  const [userData, setUserData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 50;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, user } = useSelector((state) => state.auth);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const match = location.pathname.match(/fromClient-(\d+)/);
  const clientId = match ? match[1] : null;

  // State for sorting; initially no sorting applied
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const handleCreateUser = () => {
    if (user.type === "superadmin") {
      navigate(`/admin-panel/users/create/fromClient-${clientId}`);
    } else {
      navigate("/dashboard/users/create");
    }
  };

  const fetchUserData = (pageNum, searchParam = "") => {
    setLoading(true);
    const reqBody = {
      id: user.id,
      client_id: user.type === "superadmin" ? Number(clientId) : user.client_id,
      type: user.type,
      page: pageNum.toString(),
      limit: limit.toString(),
      search_param: searchParam,
    };
    getUserData(token, reqBody)
      .then((response) => {
        setUserData(response.data.client_data || []);
        setTotalUsers(response.data.total || 0);
        setLoading(false);
        setSearchPerformed(searchParam !== "" ? true : false);
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
        setLoading(false);
      });
  };

  const handleDeleteUser = (userId) => {
    setLoading(true);
    const multipartData = new FormData();
    multipartData.append("admin_user_id", userId || "");
    multipartData.append(
      "client_id",
      (user.type === "superadmin" ? Number(clientId) : user.client_id) || "",
    );
    deleteUser(token, multipartData)
      .then((response) => {
        setLoading(false);
        console.log("response?.success", response?.data?.success === 1);
        if (response?.data?.success === 1) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Admin user deleted successfully.",
            life: 3000,
          });
        }
        fetchUserData(page, searchValue);
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
        setLoading(false);
      });
  };

  const showDeleteConfirm = (userId) => {
    confirmDialog({
      message: (
        <div>Are you sure you want to delete this user permanently?</div>
      ),
      header: "Confirm",
      position: "top",
      accept: () => handleDeleteUser(userId),
      reject: () => console.log("reject"),
      acceptClassName: "createClientBtn",
      rejectClassName: "cancel",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
    });
  };

  useEffect(() => {
    fetchUserData(page);
  }, [page]);

  const handlePrevPage = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(totalUsers / limit);
    if (page < maxPage) setPage((prev) => prev + 1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // If already sorting on this field, toggle the order
      const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newSortOrder);
    } else {
      // New sort field selected, set sort order ascending by default
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const renderHeaderAndControls = () => (
    <div
      className={`d-flex justify-content-between align-items-center px-3 py-2 ${
        userData.length > 0 || loading ? "" : "my-3"
      }`}
    >
      <h6 className="fw-bold m-0 title">View all Users</h6>
      <Button
        label="Add a user"
        className="px-3 py-2 fw-bold createClientBtn text-uppercase"
        onClick={handleCreateUser}
      />
    </div>
  );

  const debouncedSearchUser = useCallback(
    _.debounce((searchTerm) => {
      if (searchTerm.length >= 3) {
        fetchUserData(page, searchTerm);
      }
      if (searchTerm.length === 0) {
        fetchUserData(page);
      }
    }, 500),
    [token, navigate, dispatch, page],
  );

  const handleSearchUser = (event) => {
    const searchTerm = event.target.value;
    setSearchValue(searchTerm);
    debouncedSearchUser(searchTerm);
  };

  // Memoized sorted userData for rendering;
  // If no sortField or sortOrder, return data as-is (no sorting)
  const sortedUserData = useMemo(() => {
    if (!userData) return [];

    if (!sortField || !sortOrder) {
      return userData;
    }

    const data = [...userData];
    if (sortField === "first_name") {
      data.sort((a, b) => {
        const nameA = a.first_name?.toLowerCase() || "";
        const nameB = b.first_name?.toLowerCase() || "";
        if (nameA < nameB) return sortOrder === "asc" ? -1 : 1;
        if (nameA > nameB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    } else if (sortField === "categories_count") {
      data.sort((a, b) => {
        const valA = a.categories_count || 0;
        const valB = b.categories_count || 0;
        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [userData, sortField, sortOrder]);

  const renderSearchAndPagination = () => {
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, totalUsers);
    const isPrevDisabled = page === 1;
    const isNextDisabled = end >= totalUsers;

    return (
      <div className="d-flex justify-content-between align-items-center p-2">
        <div className="d-flex align-items-center">
          <span className="p-input-icon-left">
            <i
              className="pi pi-search"
              style={{ marginLeft: "320px", color: "#9e9e9e" }}
            ></i>
            <InputText
              placeholder="Search by name or email..."
              style={{ borderRadius: "0px", width: "350px", fontWeight: "400" }}
              className="inputField"
              autoComplete="off"
              name="search-user-input"
              value={searchValue}
              onChange={handleSearchUser}
            />
          </span>
        </div>
        <Pagination
          start={start}
          end={end}
          totalClients={totalUsers}
          isPrevDisabled={isPrevDisabled}
          isNextDisabled={isNextDisabled}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
        />

        <Toast ref={toast} />
      </div>
    );
  };

  return (
    <>
      <div className="container-fluid page-wrapper">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card shadow-card" style={{ minHeight: "590px" }}>
              {sortedUserData.length ? renderHeaderAndControls() : ""}
              <Divider
                style={{
                  margin: "0.5rem 0 !important",
                  padding: "0 1rem !important",
                }}
              />
              {loading || sortedUserData.length > 0 || searchPerformed ? (
                <div className="table-responsive">
                  {renderSearchAndPagination()}
                  <Divider />
                  <table className="table table-borderless clientsTable">
                    <thead className="clientsTableHead">
                      <tr className="text-muted" style={{ fontSize: "12px" }}>
                        <th style={{ width: "6%" }}>Sr #</th>
                        <th
                        // style={{ width: "30%", cursor: "pointer" }}
                        // onClick={() => handleSort("first_name")}
                        >
                          First Name{" "}
                          {/* {sortField === "first_name" ? (
                            sortOrder === "asc" ? (
                              <span className="pi pi-sort-amount-down"></span>
                            ) : (
                              <span className="pi pi-sort-amount-up"></span>
                            )
                          ) : (
                            <span className="pi pi-sort-alt"></span>
                          )} */}
                        </th>
                        <th>Last Name</th>
                        <th>Email Address</th>
                        <th>Permissions</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <ClientTableSkeleton />
                      ) : sortedUserData.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-muted">
                            No records found
                          </td>
                        </tr>
                      ) : (
                        sortedUserData.map((sortedUser, index) => (
                          <tr key={sortedUser.id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{sortedUser.first_name}</td>
                            <td>{sortedUser.last_name}</td>
                            <td>{sortedUser.email}</td>
                            <td>{sortedUser.permissions}</td>
                            <td>
                              <span
                                className="text-primary"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  if (user.type === "superadmin") {
                                    navigate(
                                      `/admin-panel/users/edit/fromClient-${clientId}-${sortedUser.id}`,
                                    );
                                  } else {
                                    navigate(
                                      `/dashboard/users/edit/${sortedUser.id}`,
                                    );
                                  }
                                }}
                              >
                                Edit
                              </span>
                              <span>
                                {"  "}|{"  "}
                              </span>
                              <span
                                className="text-primary"
                                style={{ cursor: "pointer" }}
                                onClick={() => showDeleteConfirm(sortedUser.id)}
                              >
                                Delete
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  className="d-flex flex-column justify-content-center align-items-center text-center"
                  style={{ height: "520px" }}
                >
                  <p className="text-muted fw-semibold mb-4">
                    You haven't added any users yet. Click below
                    <br /> to invite your first team member.
                  </p>
                  <Button
                    label="Add A User"
                    className="px-4 py-2 fw-bold createClientBtn"
                    onClick={handleCreateUser}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* confirm dialog */}
      <ConfirmDialog className="custom-confirm-dialog" />
    </>
  );
};

export default UsersList;
